import * as React from "react";
import {
    List,
    Datagrid,
    TextField,
    DateField,
    Edit,
    NumberField,
    NumberInput,
    SimpleForm,
    TextInput,
    ReferenceInput,
    SelectInput,
    FunctionField,
    SelectArrayInput,
    BooleanInput,
} from 'react-admin';

const ProductionTitle = props => {
    return <span>{props.record ? `${props.record.name}` : ''}</span>;
};

const rowStyle = (record, index) => ({
    backgroundColor: record.employments_quota !== null && record.active_employments_count > record.employments_quota ? '#fca5a5' : '#ffffff',
});

const toTitleCase = (str) => {
    return str
        .replace(/_/g, ' ')
        .replace(
            /\w\S*/g,
            text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
        );
};

const projectTypeChoices = [
    { id: 'high_end_tv', name: 'High End TV' },
    { id: 'major_motion_picture', name: 'Major Motion Picture' },
    { id: 'independent_motion_picture', name: 'Independent Motion Picture' },
    { id: 'unscripted', name: 'Unscripted' },
    { id: 'factual', name: 'Factual' },
    { id: 'continuing_drama', name: 'Continuing Drama' },
    { id: 'commercial', name: 'Commercial' },
    { id: 'animation', name: 'Animation' },
    { id: 'other', name: 'Other' },
];

const budgetBandChoices = [
    { id: '1', name: 'Band 1' },
    { id: '2', name: 'Band 2' },
    { id: '3', name: 'Band 3' },
    { id: '4', name: 'Band 4' },
];

const perDiemTypeChoices = [
    { id: 'meals', name: 'Meals' },
    { id: 'single', name: 'Single' },
    { id: 'shoot_non_shoot', name: 'Shoot / Non-Shoot' },
];

const timezoneChoices = [
    { id: 'UTC+0', name: 'UTC+0 — London / Edinburgh' },
    { id: 'UTC+1', name: 'UTC+1 — BST (British Summer Time)' },
    { id: 'UTC-5', name: 'UTC-5 — New York / Toronto' },
    { id: 'UTC-6', name: 'UTC-6 — Chicago / Mexico City' },
    { id: 'UTC-7', name: 'UTC-7 — Denver / Phoenix' },
    { id: 'UTC-8', name: 'UTC-8 — Los Angeles / Vancouver' },
    { id: 'UTC+1', name: 'UTC+1 — Paris / Berlin / Rome' },
    { id: 'UTC+2', name: 'UTC+2 — Athens / Helsinki' },
    { id: 'UTC+10', name: 'UTC+10 — Sydney / Melbourne' },
    { id: 'UTC+13', name: 'UTC+13 — Auckland / Wellington' },
];

const currencyChoices = [
    { id: 'GBP', name: 'GBP — British Pound' },
    { id: 'USD', name: 'USD — US Dollar' },
    { id: 'EUR', name: 'EUR — Euro' },
    { id: 'CAD', name: 'CAD — Canadian Dollar' },
    { id: 'AUD', name: 'AUD — Australian Dollar' },
    { id: 'NZD', name: 'NZD — New Zealand Dollar' },
];

export const ProductionList = props => (
    <List {...props} resource="productions">
        <Datagrid rowClick="show" rowStyle={rowStyle}>
            <TextField source="id" />
            <TextField source="company.name" label="Company" />
            <TextField source="name" />
            <TextField source="address" />
            <TextField source="project_type" label="Project Type" />
            <DateField source="created_at" />
            <TextField source="admins.length" label="Admins" />
            <FunctionField
                label="Timesheet Template"
                render={record => toTitleCase(record?.timesheet_pdf_template || 'Standard')}
            />
            <FunctionField
                label="Disabled Fields"
                render={record => {
                    if (!record.disabled_fields || record.disabled_fields.length === 0) {
                        return 'None';
                    }

                    return record.disabled_fields.map(field =>
                        field.charAt(0).toUpperCase() + field.slice(1)
                    ).join(', ').replaceAll('_', ' ');
                }}
            />
            <FunctionField label="Employments Quota" render={record => record.employments_quota ?? '—'} />
            <NumberField source="active_employments_count" label="Active Employments" />
        </Datagrid>
    </List>
);

export const ProductionEdit = props => {
    const transform = (data) => {
        data.company_id = data.company ? data.company.id : undefined;
        delete data.company;
        delete data.admins;
        delete data.timesheet_weeks;
        delete data.departments;
        delete data.penalty_types;
        delete data.active_employments_count;
        return data;
    };

    const timesheetTemplateChoices = [
        { id: 'standard', value: 'Standard' },
        { id: 'single_per_diem', value: 'Single Per Diem' },
        { id: 'shooting_per_diem', value: 'Shooting Per Diem' },
    ];

    return (
        <Edit {...props} title={<ProductionTitle />} undoable={false} transform={transform}>
            <SimpleForm>
                <TextInput source="name" fullWidth />
                <TextInput multiline source="address" fullWidth />
                <ReferenceInput label="Company" source="company.id" reference="companies" allowEmpty>
                    <SelectInput optionText="name" />
                </ReferenceInput>
                <SelectInput source="project_type" choices={projectTypeChoices} allowEmpty />
                <SelectInput source="budget_band" choices={budgetBandChoices} allowEmpty helperText="HETV only" />
                <TextInput source="other_description" fullWidth helperText="Required when project type is Other" />

                <BooleanInput source="base_address_same_as_registered" label="Base address same as registered" />
                <TextInput source="base_address_1" label="Base Address Line 1" fullWidth />
                <TextInput source="base_address_2" label="Base Address Line 2" fullWidth />
                <TextInput source="base_address_city" label="Base City" />
                <TextInput source="base_address_county" label="Base County" />
                <TextInput source="base_address_postcode" label="Base Postcode" />
                <TextInput source="base_address_country" label="Base Country" />

                <SelectInput source="timezone" choices={timezoneChoices} allowEmpty />
                <SelectInput source="currency" choices={currencyChoices} allowEmpty />

                <NumberInput source="mileage_rate" helperText="Per mile rate" />
                <NumberInput source="holiday_pay_percentage" helperText="e.g. 12.07" />
                <NumberInput source="meal_allowance_rate" />

                <SelectInput source="per_diem_type" choices={perDiemTypeChoices} allowEmpty />
                <NumberInput source="per_diem_breakfast_rate" />
                <NumberInput source="per_diem_lunch_rate" />
                <NumberInput source="per_diem_dinner_rate" />
                <NumberInput source="per_diem_single_rate" />
                <NumberInput source="per_diem_non_shoot_rate" />
                <NumberInput source="per_diem_shoot_rate" />

                <SelectInput source="timesheet_pdf_template" choices={timesheetTemplateChoices} optionText="value" optionValue="id" />
                <SelectArrayInput
                    source="disabled_fields"
                    choices={[
                        { id: 'mileage', name: 'Mileage' },
                        { id: 'allowance', name: 'Allowance' },
                        { id: 'consecutive_days', name: 'Consecutive Days' },
                    ]}
                    multiple={true}
                />
                <NumberInput source="employments_quota" helperText="Leave empty to not enforce a quota" />
            </SimpleForm>
        </Edit>
    );
};